import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip
} from '@heroui/react';
import type { UserInvitation } from '@/types/user-invitation.model';

interface InvitationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  invitation: UserInvitation | null;
  onResend: (invitation: UserInvitation) => void;
  onDelete: (invitation: UserInvitation) => void;
}

const InvitationDetailModal: React.FC<InvitationDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  invitation,
  onResend,
  onDelete
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleResend = async () => {
    if (!invitation) return;
    
    setIsProcessing(true);
    try {
      onResend(invitation);
      onClose();
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      setErrors({ submit: 'Error al reenviar la invitación. Por favor intente nuevamente.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!invitation) return;
    
    setIsProcessing(true);
    try {
      onDelete(invitation);
      onClose();
    } catch (error: any) {
      console.error('Error deleting invitation:', error);
      setErrors({ submit: 'Error al eliminar la invitación. Por favor intente nuevamente.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!invitation) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      placement="top-center"
      backdrop="blur"
    >
      <ModalContent>
        {(_onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              Detalles de la Invitación
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <div className="pt-2">
                  <p className="text-sm text-gray-500">Información de la invitación</p>
                  <div className="mt-1 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Nombre:</span>
                      <span className="text-sm font-medium">{invitation.nombre || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Email:</span>
                      <span className="text-sm font-medium">{invitation.email || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Rol:</span>
                      <Chip 
                        color="primary" 
                        variant="flat"
                        size="sm"
                      >
                        {invitation.role || '-'}
                      </Chip>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Estado:</span>
                      <Chip 
                        color={invitation.estado === 'pending' ? 'warning' : 'success'} 
                        variant="flat"
                        size="sm"
                      >
                        {invitation.estado || '-'}
                      </Chip>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Fecha de creación:</span>
                      <span className="text-sm font-medium">{invitation.createdAt ? new Date(invitation.createdAt).toLocaleDateString() : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">ID de documento:</span>
                      <span className="text-sm font-mono">{invitation.documentId || '-'}</span>
                    </div>
                  </div>
                </div>
                
                {errors.submit && (
                  <div className="mt-4 p-3 bg-danger/20 text-danger text-sm rounded-md">
                    {errors.submit}
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter className="flex justify-between">
              <Button 
                color="danger" 
                variant="flat" 
                onClick={handleClose}
              >
                Cerrar
              </Button>
              <div className="space-x-2">
                <Button 
                  color="primary" 
                  variant="flat"
                  onClick={handleResend}
                  isLoading={isProcessing}
                  isDisabled={isProcessing}
                >
                  Reenviar
                </Button>
                <Button 
                  color="danger" 
                  onClick={handleDelete}
                  isLoading={isProcessing}
                  isDisabled={isProcessing}
                >
                  Eliminar
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default InvitationDetailModal;